import * as React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import CompassLogo from "./CompassLogo";
import { useDocument } from "@/hooks/useDocument";
import { useDictation } from "@/hooks/useDictation";
import {
  useCompassChatHistory,
  useInvalidateCompassChat,
  transformCompassHistory,
} from "@/hooks/useCompassChat";
import { forgetCompassSendTime, rememberCompassSendTime } from "@/hooks/compass-chat.utils";
import { compasAIQueryStream } from "@/services/compass.service";
import {
  compassStreamUserError,
  createCompassDeadline,
  processCompassStream,
  upsertOptimisticAssistant,
  type CompassStreamResult,
} from "./CompassDialog.utils";
import { CompassMessagesArea, CompassInputArea } from "./CompassDialogSubComponents";
import type { ICompasAIQueryRequest, ICompassMessage } from "@/types/compass.types";
import { toast } from "sonner";
import { debugError } from "@/utils/debug";
import {
  getBackendDocumentId,
  getDocumentTitle,
  notifyDocumentNotReady,
} from "@/utils/document-id.utils";

interface CompassDialogProps {
  isOpen: boolean;
  onClose: () => void;
  isTabActive?: boolean;
}

const useCompassMessages = (
  historyMessages: ICompassMessage[],
  optimisticMessages: ICompassMessage[]
) => {
  return React.useMemo(
    () => [...historyMessages, ...optimisticMessages],
    [historyMessages, optimisticMessages]
  );
};

const useAutoScrollToBottom = (
  messagesLength: number,
  isAssistantTyping: boolean,
  messagesEndRef: React.RefObject<HTMLDivElement | null>
) => {
  const prevMessagesLengthRef = React.useRef(0);
  React.useEffect(() => {
    if (messagesLength > prevMessagesLengthRef.current || isAssistantTyping)
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    prevMessagesLengthRef.current = messagesLength;
  }, [messagesLength, isAssistantTyping]);
};

const buildUserMessage = (userMessage: string): ICompassMessage => {
  const creation_date = new Date().toISOString();
  rememberCompassSendTime(userMessage, creation_date);
  return { isSender: true, canCopy: false, text: userMessage, creation_date };
};

const resetTextareaHeight = (textareaRef: React.RefObject<HTMLTextAreaElement | null>) => {
  if (textareaRef.current) textareaRef.current.style.height = "20px";
};

async function sendCompassQuery(
  payload: ICompasAIQueryRequest,
  onText: (text: string) => void
): Promise<CompassStreamResult> {
  const deadline = createCompassDeadline();
  try {
    const stream = await compasAIQueryStream(payload, deadline.signal);
    return await processCompassStream(stream, {
      signal: deadline.signal,
      timeoutMs: 0,
      onText,
    });
  } finally {
    deadline.cancel();
  }
}

const useSendCompassMessage = (
  chatMessage: string,
  doc: ReturnType<typeof useDocument>["data"],
  setChatMessage: React.Dispatch<React.SetStateAction<string>>,
  setIsAssistantTyping: React.Dispatch<React.SetStateAction<boolean>>,
  setOptimisticMessages: React.Dispatch<React.SetStateAction<ICompassMessage[]>>,
  invalidateCompassChat: ReturnType<typeof useInvalidateCompassChat>,
  textareaRef: React.RefObject<HTMLTextAreaElement | null>
) => {
  const handleSendMessage = async () => {
    if (!chatMessage.trim()) return;
    const documentId = getBackendDocumentId(doc);
    if (documentId == null) {
      notifyDocumentNotReady("compass");
      return;
    }
    const userMessage = chatMessage.trim();
    const optimistic = buildUserMessage(userMessage);
    setChatMessage("");
    resetTextareaHeight(textareaRef);
    setOptimisticMessages((prev) => [...prev, optimistic]);
    setIsAssistantTyping(true);
    try {
      const result = await sendCompassQuery(
        {
          text: userMessage,
          document_ids: [documentId],
          current_document_id: documentId,
          document_title: [getDocumentTitle(doc)],
        },
        (text) => setOptimisticMessages((prev) => upsertOptimisticAssistant(prev, text))
      );
      const streamError = compassStreamUserError(result);
      if (streamError != null) toast.error("Compass couldn't reply", { description: streamError });
      else if (result.text.trim() !== "") {
        setOptimisticMessages((prev) => upsertOptimisticAssistant(prev, result.text));
      }
      await invalidateCompassChat(documentId);
      setOptimisticMessages([]);
    } catch (error) {
      debugError("compass", "send failed", {
        name: error instanceof Error ? error.name : "unknown",
        message: error instanceof Error ? error.message : String(error),
      });
      toast.error("Compass couldn't reply", {
        description: error instanceof Error ? error.message : "Please try again.",
      });
      forgetCompassSendTime(userMessage, optimistic.creation_date);
      setOptimisticMessages([]);
    } finally {
      setIsAssistantTyping(false);
    }
  };
  return handleSendMessage;
};

const buildTextareaResizeHandler = (
  setChatMessage: React.Dispatch<React.SetStateAction<string>>
) => {
  return (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setChatMessage(event.target.value);
    const ta = event.target;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
  };
};

const useCompassDialogState = (isOpen: boolean, isTabActive: boolean) => {
  const [chatMessage, setChatMessage] = React.useState("");
  const [isAssistantTyping, setIsAssistantTyping] = React.useState(false);
  const [optimisticMessages, setOptimisticMessages] = React.useState<ICompassMessage[]>([]);
  const { data: doc } = useDocument();
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const { data: chatHistory, isLoading } = useCompassChatHistory(getBackendDocumentId(doc) ?? "");
  const { isRecording, isMicroLoading, isConverting, startDictation, isSpeaking } = useDictation({
    onTranscript: setChatMessage,
    isActive: isOpen && isTabActive,
  });
  const invalidateCompassChat = useInvalidateCompassChat();
  const handleSendMessage = useSendCompassMessage(
    chatMessage,
    doc,
    setChatMessage,
    setIsAssistantTyping,
    setOptimisticMessages,
    invalidateCompassChat,
    textareaRef
  );
  React.useEffect(() => {
    if (!textareaRef.current) return;
    const ta = textareaRef.current;
    const sc = scrollContainerRef.current;
    const near = sc ? sc.scrollHeight - sc.scrollTop <= sc.clientHeight + 50 : false;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
    if (near && sc) sc.scrollTop = sc.scrollHeight;
  }, [chatMessage, isSpeaking]);
  return {
    chatMessage,
    isAssistantTyping,
    optimisticMessages,
    doc,
    messagesEndRef,
    textareaRef,
    scrollContainerRef,
    isLoading,
    historyMessages: transformCompassHistory(chatHistory),
    isRecording,
    isMicroLoading,
    isConverting,
    startDictation,
    isSpeaking,
    handleSendMessage,
    handleTextareaChange: buildTextareaResizeHandler(setChatMessage),
  };
};

const CompassDialog: React.FC<CompassDialogProps> = ({ isOpen, onClose, isTabActive = true }) => {
  const s = useCompassDialogState(isOpen, isTabActive);
  const messages = useCompassMessages(s.historyMessages, s.optimisticMessages);
  useAutoScrollToBottom(messages.length, s.isAssistantTyping, s.messagesEndRef);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl w-11/12 max-h-[90vh] h-full flex flex-col p-0">
        <DialogHeader className="flex-row items-center justify-between px-4 py-2 border-b">
          <div className="flex items-center gap-3">
            <CompassLogo containerClassName="size-10" />
            <DialogTitle className="text-base font-medium">AI Compass</DialogTitle>
          </div>
        </DialogHeader>
        <div className="flex-1 flex flex-col min-h-0">
          <CompassMessagesArea
            messages={messages}
            isLoading={s.isLoading}
            isAssistantTyping={s.isAssistantTyping}
            scrollContainerRef={s.scrollContainerRef}
            messagesEndRef={s.messagesEndRef}
          />
          <CompassInputArea
            isRecording={s.isRecording}
            isMicroLoading={s.isMicroLoading}
            isSpeaking={s.isSpeaking}
            isConverting={s.isConverting}
            chatMessage={s.chatMessage}
            isAssistantTyping={s.isAssistantTyping}
            textareaRef={s.textareaRef}
            handleTextareaChange={s.handleTextareaChange}
            handleSendMessage={s.handleSendMessage}
            startDictation={s.startDictation}
            documentReady={getBackendDocumentId(s.doc) != null}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CompassDialog;
